import React, { useState, useMemo, useEffect, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Autocomplete,
  Collapse,
  Checkbox,
  FormGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Home as HomeIcon,
  ElderlyWoman as ElderlyIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from "@mui/icons-material";
import { signupAPI } from "../services/api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const steps = ["Account Type", "Your Information", "Their Information", "Terms of Service", "Review & Pay"];
const stepsForSelf = ["Account Type", "Your Information", "Terms of Service", "Review & Pay"];

// US States list
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

// Password requirements
const passwordRequirements = [
  { label: "At least 8 characters", test: (pw) => pw.length >= 8 },
  { label: "Contains uppercase letter", test: (pw) => /[A-Z]/.test(pw) },
  { label: "Contains lowercase letter", test: (pw) => /[a-z]/.test(pw) },
  { label: "Contains a number", test: (pw) => /[0-9]/.test(pw) },
  { label: "Contains a special character (!@#$%^&*...)", test: (pw) => /[!@#$%^&*(),.?":{}|<>_\-+=]/.test(pw) },
];

// Phone number formatting
const formatPhoneInput = (value) => {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length === 0) return "";
  if (numbers.length <= 3) return `(${numbers}`;
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

// ZIP code formatting (5 digits only)
const formatZipInput = (value) => {
  return value.replace(/\D/g, "").slice(0, 5);
};

// Email validation
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const Signup = () => {
  const [signupsEnabled, setSignupsEnabled] = useState(true);
  const [statusLoading, setStatusLoading] = useState(true);
  const [pauseMessage, setPauseMessage] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipError, setZipError] = useState("");
  const [showManualAddress, setShowManualAddress] = useState(false);

  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [acknowledgments, setAcknowledgments] = useState({
    advisory: false,
    noGuarantee: false,
    responsibility: false,
    noProfessional: false,
    termsPrivacy: false,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const formTopRef = useRef(null);

  const [formData, setFormData] = useState({
    // Account type
    accountType: "", // "self" or "other"
    
    // User (account owner) info
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    
    // Address (for billing)
    address: "",
    city: "",
    state: null, // Will be state object { code, name }
    zipCode: "",
    
    // Client info (if signing up for someone else)
    clientFirstName: "",
    clientLastName: "",
    clientPhone: "",
  });

  // Field-level errors for real-time validation
  const [fieldErrors, setFieldErrors] = useState({});
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [checkingClientPhone, setCheckingClientPhone] = useState(false);
  const [validating, setValidating] = useState(false);

  // AbortControllers for cancelling stale validation requests
  const emailCheckRef = useRef(null);
  const phoneCheckRef = useRef(null);
  const clientPhoneCheckRef = useRef(null);

  useEffect(() => {
    const checkSignupStatus = async () => {
      try {
        const response = await signupAPI.checkStatus();
        setSignupsEnabled(response.data.signupsAvailable);
        setPauseMessage(response.data.message || '');
      } catch (err) {
        // Fail open — don't block signups on network error
        setSignupsEnabled(true);
      } finally {
        setStatusLoading(false);
      }
    };
    checkSignupStatus();
  }, []);

  const handleWaitlistSubmit = async () => {
    if (!waitlistEmail.trim() || !isValidEmail(waitlistEmail)) {
      setWaitlistError('Please enter a valid email address');
      return;
    }
    setWaitlistSubmitting(true);
    setWaitlistError('');
    try {
      await signupAPI.joinWaitlist({
        email: waitlistEmail.trim(),
        name: waitlistName.trim() || undefined,
        source: 'signup_page_paused',
      });
      setWaitlistSuccess(true);
    } catch (err) {
      setWaitlistError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const currentSteps = formData.accountType === "self" ? stepsForSelf : steps;

  // Password validation status
  const passwordValidation = useMemo(() => {
    return passwordRequirements.map((req) => ({
      ...req,
      met: req.test(formData.password),
    }));
  }, [formData.password]);

  const allPasswordRequirementsMet = passwordValidation.every((req) => req.met);

  // Lookup city/state from ZIP code (fallback when Google not available)
  const lookupZip = async (zip) => {
    if (zip.length !== 5) return;
    
    setZipLoading(true);
    setZipError("");
    
    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (response.ok) {
        const data = await response.json();
        const place = data.places[0];
        const stateCode = place["state abbreviation"];
        const city = place["place name"];
        
        // Find the state object
        const stateObj = US_STATES.find(s => s.code === stateCode);
        
        setFormData(prev => ({
          ...prev,
          city: city,
          state: stateObj || null,
        }));
      } else {
        setZipError("Invalid ZIP code");
      }
    } catch (err) {
      console.error("ZIP lookup failed:", err);
      // Don't show error - just let user enter manually
    } finally {
      setZipLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear field-specific error when user types
    setFieldErrors(prev => ({ ...prev, [name]: "" }));
    setError("");
    
    // Auto-format phone numbers
    if (name === "phone" || name === "clientPhone") {
      setFormData({
        ...formData,
        [name]: formatPhoneInput(value),
      });
    } 
    // Format ZIP and trigger lookup
    else if (name === "zipCode") {
      const formattedZip = formatZipInput(value);
      setFormData({
        ...formData,
        zipCode: formattedZip,
      });
      // Lookup when 5 digits entered (only if Google didn't fill it)
      if (formattedZip.length === 5 && !formData.city) {
        lookupZip(formattedZip);
      }
    }
    // Validate email on blur handled separately
    else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleEmailBlur = async () => {
    if (formData.email && !isValidEmail(formData.email)) {
      setFieldErrors(prev => ({ ...prev, email: "Please enter a valid email address" }));
      return;
    }
    if (formData.email && isValidEmail(formData.email)) {
      // Cancel any in-flight email check
      if (emailCheckRef.current) emailCheckRef.current.abort();
      const controller = new AbortController();
      emailCheckRef.current = controller;

      setCheckingEmail(true);
      try {
        const response = await signupAPI.checkEmail(formData.email, { signal: controller.signal });
        if (!controller.signal.aborted && !response.data.available) {
          setFieldErrors(prev => ({ ...prev, email: 'already_in_use' }));
        }
      } catch (err) {
        // Fail silently — final submit is the safety net (ignore aborted requests too)
      } finally {
        if (!controller.signal.aborted) setCheckingEmail(false);
      }
    }
  };

  const handlePhoneBlur = async () => {
    const digits = formData.phone.replace(/\D/g, "");
    if (digits.length >= 10) {
      // Cancel any in-flight phone check
      if (phoneCheckRef.current) phoneCheckRef.current.abort();
      const controller = new AbortController();
      phoneCheckRef.current = controller;

      setCheckingPhone(true);
      try {
        const response = await signupAPI.checkPhone(digits, { signal: controller.signal });
        if (!controller.signal.aborted && !response.data.available) {
          if (response.data.reason === 'linked_to_caregiver') {
            setFieldErrors(prev => ({ ...prev, phone: 'linked_to_caregiver', phoneCaregiverName: response.data.caregiverFirstName }));
          } else {
            setFieldErrors(prev => ({ ...prev, phone: 'already_in_use' }));
          }
        }
      } catch (err) {
        // Fail silently — final submit is the safety net (ignore aborted requests too)
      } finally {
        if (!controller.signal.aborted) setCheckingPhone(false);
      }
    }
  };

  const handleClientPhoneBlur = async () => {
    const digits = formData.clientPhone.replace(/\D/g, "");
    if (digits.length >= 10) {
      if (clientPhoneCheckRef.current) clientPhoneCheckRef.current.abort();
      const controller = new AbortController();
      clientPhoneCheckRef.current = controller;

      setCheckingClientPhone(true);
      try {
        const response = await signupAPI.checkPhone(digits, { signal: controller.signal });
        if (!controller.signal.aborted && !response.data.available) {
          if (response.data.reason === 'linked_to_caregiver') {
            setFieldErrors(prev => ({ ...prev, clientPhone: 'linked_to_caregiver', clientPhoneCaregiverName: response.data.caregiverFirstName }));
          } else {
            setFieldErrors(prev => ({ ...prev, clientPhone: 'already_in_use' }));
          }
        }
      } catch (err) {
        // Fail silently — final submit is the safety net
      } finally {
        if (!controller.signal.aborted) setCheckingClientPhone(false);
      }
    }
  };

  const handleStateChange = (event, newValue) => {
    setFormData({
      ...formData,
      state: newValue,
    });
  };

  const handleTermsScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAcknowledgmentChange = (key) => {
    setAcknowledgments(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const allAcknowledged = Object.values(acknowledgments).every(Boolean);

  const dashboardUrl = process.env.REACT_APP_DASHBOARD_URL || "https://dashboard.secondclear.com";

  const validateStep = async () => {
    switch (activeStep) {
      case 0: // Account Type
        if (!formData.accountType) {
          setError("Please select who you're signing up for");
          return false;
        }
        return true;

      case 1: { // Your Information
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.password) {
          setError("Please fill in all required fields");
          return false;
        }
        if (!isValidEmail(formData.email)) {
          setError("Please enter a valid email address");
          return false;
        }
        if (!allPasswordRequirementsMet) {
          setError("Please meet all password requirements");
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError("Passwords do not match");
          return false;
        }
        if (!formData.address || !formData.city || !formData.state || !formData.zipCode) {
          setError("Please fill in your complete billing address");
          return false;
        }
        if (formData.zipCode.length !== 5) {
          setError("Please enter a valid 5-digit ZIP code");
          return false;
        }
        // Validate phone has enough digits
        const phoneDigits = formData.phone.replace(/\D/g, "");
        if (phoneDigits.length < 10) {
          setError("Please enter a valid 10-digit phone number");
          return false;
        }

        // Run uniqueness checks inline (prevents race condition with blur)
        const newErrors = { ...fieldErrors };
        try {
          const emailRes = await signupAPI.checkEmail(formData.email);
          if (!emailRes.data.available) {
            newErrors.email = 'already_in_use';
          }
        } catch {}
        try {
          const phoneRes = await signupAPI.checkPhone(phoneDigits);
          if (!phoneRes.data.available) {
            if (phoneRes.data.reason === 'linked_to_caregiver') {
              newErrors.phone = 'linked_to_caregiver';
              newErrors.phoneCaregiverName = phoneRes.data.caregiverFirstName;
            } else {
              newErrors.phone = 'already_in_use';
            }
          }
        } catch {}
        setFieldErrors(newErrors);

        if (newErrors.email || newErrors.phone) {
          // Don't set a top-level error — field-level messages will show
          return false;
        }
        return true;
      }

      case 2: // Self: Terms of Service, Other: Their Information
        if (formData.accountType === "self") {
          // ToS validation for self flow
          if (!hasScrolledToBottom) {
            setError("Please read the complete Terms of Service");
            return false;
          }
          if (!allAcknowledged) {
            setError("Please check all acknowledgment boxes to continue");
            return false;
          }
        } else {
          if (!formData.clientFirstName || !formData.clientLastName || !formData.clientPhone) {
            setError("Please enter their name and phone number");
            return false;
          }
          const clientPhoneDigits = formData.clientPhone.replace(/\D/g, "");
          if (clientPhoneDigits.length < 10) {
            setError("Please enter a valid 10-digit phone number");
            return false;
          }

          // Check if client phone is already registered
          const clientErrors = { ...fieldErrors };
          try {
            const clientPhoneRes = await signupAPI.checkPhone(clientPhoneDigits);
            if (!clientPhoneRes.data.available) {
              if (clientPhoneRes.data.reason === 'linked_to_caregiver') {
                clientErrors.clientPhone = 'linked_to_caregiver';
                clientErrors.clientPhoneCaregiverName = clientPhoneRes.data.caregiverFirstName;
              } else {
                clientErrors.clientPhone = 'already_in_use';
              }
            }
          } catch {}
          setFieldErrors(clientErrors);

          if (clientErrors.clientPhone) {
            return false;
          }
        }
        return true;

      case 3: // Other: Terms of Service
        if (formData.accountType === "other") {
          if (!hasScrolledToBottom) {
            setError("Please read the complete Terms of Service");
            return false;
          }
          if (!allAcknowledged) {
            setError("Please check all acknowledgment boxes to continue");
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const scrollToTop = () => {
    if (formTopRef.current) {
      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNext = async () => {
    setValidating(true);
    try {
      if (await validateStep()) {
        setActiveStep((prev) => prev + 1);
        scrollToTop();
      }
    } finally {
      setValidating(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError("");
    scrollToTop();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      const stripe = await stripePromise;

      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      const clientFullName = formData.accountType === "other" 
        ? `${formData.clientFirstName} ${formData.clientLastName}`.trim()
        : null;

      // Create checkout session
      const response = await signupAPI.createCheckoutSession({
        accountType: formData.accountType,
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: fullName,
        email: formData.email,
        phone: formData.phone.replace(/\D/g, ""), // Send digits only
        password: formData.password,
        address: {
          line1: formData.address,
          city: formData.city,
          state: formData.state?.code || "", // Send state code
          postal_code: formData.zipCode,
          country: "US",
        },
        client: formData.accountType === "other" ? {
          firstName: formData.clientFirstName,
          lastName: formData.clientLastName,
          name: clientFullName,
          phone: formData.clientPhone.replace(/\D/g, ""), // Send digits only
        } : null,
        termsAccepted: true,
        termsVersion: "2026-04",
        termsAcceptedAt: new Date().toISOString(),
      });

      const { sessionId } = response.data;

      // Redirect to Stripe Checkout
      const result = await stripe.redirectToCheckout({ sessionId });

      if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      // Special handling for email already exists
      if (errorData?.code === "EMAIL_EXISTS") {
        setError(
          <span>
            This email is already associated with an account.{" "}
            <a href={`${process.env.REACT_APP_DASHBOARD_URL || "https://dashboard.secondclear.com"}/forgot-password`} style={{ color: "#1976d2" }}>
              Forgot your password?
            </a>
          </span>
        );
      } else {
        setError(errorData?.error || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" gutterBottom align="center">
              Who are you signing up for?
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
              Second Clear helps verify suspicious calls and texts
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                name="accountType"
                value={formData.accountType}
                onChange={handleChange}
              >
                <Paper
                  elevation={formData.accountType === "self" ? 3 : 1}
                  sx={{
                    p: 3,
                    mb: 2,
                    cursor: "pointer",
                    border: formData.accountType === "self" ? "2px solid #1976d2" : "2px solid transparent",
                    "&:hover": { borderColor: "#1976d2" },
                  }}
                  onClick={() => setFormData({ ...formData, accountType: "self" })}
                >
                  <FormControlLabel
                    value="self"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          I'm signing up for myself
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          I want to verify suspicious calls and texts I receive
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>

                <Paper
                  elevation={formData.accountType === "other" ? 3 : 1}
                  sx={{
                    p: 3,
                    cursor: "pointer",
                    border: formData.accountType === "other" ? "2px solid #1976d2" : "2px solid transparent",
                    "&:hover": { borderColor: "#1976d2" },
                  }}
                  onClick={() => setFormData({ ...formData, accountType: "other" })}
                >
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          I'm signing up for a family member or client
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          I want to help a parent, relative, or client stay safe
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Your Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This will be used to create your account
            </Typography>

            <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                margin="normal"
                required
              />
            </Box>

            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              margin="normal"
              required
              error={!!fieldErrors.email}
              helperText={
                fieldErrors.email === 'already_in_use' ? (
                  <span>
                    This email is already associated with an account.{" "}
                    Already have an account?{" "}
                    <a href={`${dashboardUrl}/login`} style={{ color: "#1976d2" }}>Sign in</a>
                  </span>
                ) : fieldErrors.email || ""
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: checkingEmail ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />

            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handlePhoneBlur}
              margin="normal"
              required
              placeholder="(555) 555-5555"
              error={!!fieldErrors.phone}
              helperText={
                fieldErrors.phone === 'linked_to_caregiver' ? (
                  <span>
                    This phone number is already linked to {fieldErrors.phoneCaregiverName ? `${fieldErrors.phoneCaregiverName}'s` : "a caregiver's"} account.
                    {" "}They will need to remove you from their account first.
                    {" "}If this is an error, please contact us at help@secondclear.com
                  </span>
                ) : fieldErrors.phone === 'already_in_use' ? (
                  <span>
                    This phone number is already registered.{" "}
                    Already have an account?{" "}
                    <a href={`${dashboardUrl}/login`} style={{ color: "#1976d2" }}>Sign in</a>
                  </span>
                ) : fieldErrors.phone || ""
              }
              inputProps={{ maxLength: 14 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: checkingPhone ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Requirements Checklist */}
            {formData.password && (
              <Paper variant="outlined" sx={{ p: 2, mt: 1, mb: 2, bgcolor: "#fafafa" }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Password Requirements:
                </Typography>
                <List dense disablePadding>
                  {passwordValidation.map((req, index) => (
                    <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {req.met ? (
                          <CheckIcon fontSize="small" color="success" />
                        ) : (
                          <CloseIcon fontSize="small" color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={req.label}
                        primaryTypographyProps={{
                          variant: "body2",
                          color: req.met ? "success.main" : "text.secondary",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              margin="normal"
              required
              error={formData.confirmPassword && formData.password !== formData.confirmPassword}
              helperText={
                formData.confirmPassword && formData.password !== formData.confirmPassword
                  ? "Passwords do not match"
                  : ""
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }} fontWeight={600}>
              Billing Address
            </Typography>

            <TextField
              fullWidth
              label="Street Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              margin="normal"
              required
              placeholder="Enter your street address"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HomeIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />


            {/* Manual address fields - always shown if no auto-filled data or expanded */}
            <Collapse in={true}>
              <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1 }}>
                <TextField
                  label="ZIP Code"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  margin="normal"
                  required
                  sx={{ width: { xs: '100%', sm: '130px' } }}
                  inputProps={{ maxLength: 5 }}
                  error={!!zipError}
                  helperText={zipError}
                  InputProps={{
                    endAdornment: zipLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <Autocomplete
                  options={US_STATES}
                  getOptionLabel={(option) => option.code}
                  value={formData.state}
                  onChange={handleStateChange}
                  renderOption={(props, option) => (
                    <li {...props} key={option.code}>
                      {option.code} - {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      margin="normal"
                      required
                    />
                  )}
                  sx={{ width: { xs: '100%', sm: '130px' } }}
                  disableClearable
                />
              </Box>
      
            </Collapse>
          </Box>
        );

      case 2:
        if (formData.accountType === "self") {
          // Self flow: step 2 = Terms of Service
          return renderTermsStep();
        }
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Their Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the phone number of the person you want to help.
              They will receive a verification text to confirm.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                name="clientFirstName"
                value={formData.clientFirstName}
                onChange={handleChange}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ElderlyIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="clientLastName"
                value={formData.clientLastName}
                onChange={handleChange}
                margin="normal"
                required
              />
            </Box>

            <TextField
              fullWidth
              label="Phone Number"
              name="clientPhone"
              type="tel"
              value={formData.clientPhone}
              onChange={handleChange}
              onBlur={handleClientPhoneBlur}
              margin="normal"
              required
              placeholder="(555) 555-5555"
              inputProps={{ maxLength: 14 }}
              error={!!fieldErrors.clientPhone}
              helperText={
                fieldErrors.clientPhone === 'linked_to_caregiver' ? (
                  <span>
                    This phone number is already linked to {fieldErrors.clientPhoneCaregiverName ? `${fieldErrors.clientPhoneCaregiverName}'s` : "a caregiver's"} account.
                    {" "}They will need to remove this person from their account first.
                    {" "}If this is an error, please contact us at help@secondclear.com
                  </span>
                ) : fieldErrors.clientPhone === 'already_in_use' ? (
                  <span>
                    This phone number is already registered in Second Clear.{" "}
                    Already have an account?{" "}
                    <a href={`${dashboardUrl}/login`} style={{ color: "#1976d2" }}>Sign in</a>
                  </span>
                ) : fieldErrors.clientPhone || "This phone will receive a verification text message"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color={fieldErrors.clientPhone ? "error" : "action"} />
                  </InputAdornment>
                ),
                endAdornment: checkingClientPhone ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />

            <Alert severity="info" sx={{ mt: 3 }}>
              After signup, we'll send a text to this number asking them to confirm.
              They must reply YES to activate the service.
            </Alert>
          </Box>
        );

      case 3:
        if (formData.accountType === "self") {
          // Self flow: step 3 = Review & Pay
          return renderReview();
        }
        // Other flow: step 3 = Terms of Service
        return renderTermsStep();

      case 4:
        // Other flow: step 4 = Review & Pay
        return renderReview();

      default:
        return null;
    }
  };

  const renderTermsStep = () => {
    const acknowledgmentItems = [
      { key: "advisory", label: <>I understand that Second Clear is an <strong>advisory service only</strong> and does not make decisions for me.</> },
      { key: "noGuarantee", label: <>I understand that Second Clear <strong>does not guarantee outcomes</strong> and cannot confirm whether any communication is fraudulent or safe.</> },
      { key: "responsibility", label: <>I understand that <strong>I am solely responsible</strong> for any decisions or actions I take based on Second Clear's explanations.</> },
      { key: "noProfessional", label: <>I understand that Second Clear is <strong>not a substitute</strong> for legal, financial, or law enforcement advice.</> },
      { key: "termsPrivacy", label: <>I have read and agree to the <a href="https://www.secondclear.com/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="https://www.secondclear.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</> },
    ];

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Terms of Service
        </Typography>

        {/* Important Banner */}
        <Box sx={{
          bgcolor: "#fff3cd",
          borderLeft: "4px solid #ffc107",
          p: 2,
          mb: 2,
          borderRadius: 1,
        }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            IMPORTANT — PLEASE READ CAREFULLY
          </Typography>
          <Typography variant="body2">
            Second Clear is an advisory information service only. We do not guarantee outcomes, make determinations, or provide legal, financial, or law enforcement services. You are solely responsible for your own decisions.
          </Typography>
        </Box>

        {/* Scrollable ToS Container */}
        <Box sx={{ position: 'relative', mb: 1 }}>
          {!hasScrolledToBottom && (
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 48,
              background: 'linear-gradient(transparent, rgba(250,250,250,0.95))',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              pb: 0.5,
              pointerEvents: 'none',
              zIndex: 1,
              borderRadius: '0 0 8px 8px',
            }}>
              <Typography variant="caption" color="primary.main" fontWeight={600} sx={{ pointerEvents: 'none' }}>
                Scroll down to continue
              </Typography>
            </Box>
          )}
        <Box
          onScroll={handleTermsScroll}
          sx={{
            height: isMobile ? 300 : 400,
            overflowY: "scroll",
            border: hasScrolledToBottom ? "1px solid #ddd" : "2px solid #1976d2",
            borderRadius: 2,
            p: 2.5,
            bgcolor: "#fafafa",
            fontSize: 14,
            lineHeight: 1.6,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom>SECOND CLEAR — TERMS OF SERVICE</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom><strong>Last Updated: April 2026</strong></Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>These Terms of Service ("Terms") govern your use of Second Clear. By accessing or using the service, you agree to these Terms.</Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>IMPORTANT — PLEASE READ BEFORE USING SECOND CLEAR</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}><strong>SECOND CLEAR IS AN ADVISORY INFORMATION SERVICE ONLY. WE PROVIDE DATA AND ANALYSIS FOR YOUR INFORMATIONAL PURPOSES; WE DO NOT PROVIDE PROTECTION, INSURANCE, OR GUARANTEES OF SAFETY.</strong></Typography>
          <Typography variant="body2" sx={{ mt: 1 }}><strong>WE DO NOT MAKE DETERMINATIONS. WE DO NOT PROVIDE LEGAL, FINANCIAL, OR LAW ENFORCEMENT SERVICES.</strong></Typography>

          <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 2, mt: 2, mb: 1, bgcolor: '#f9f9f9' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
              <strong>The "Plain English" Summary:</strong> We're an advisor, not a bodyguard. We help you spot the "tells" and red flags, but you are still the boss of your money and your decisions. If something feels wrong, we help you take a second to see why, but the final call is always yours.
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mt: 2 }}>By using Second Clear, you acknowledge that:</Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>All reviews and explanations are <strong>advisory only</strong> and are not guarantees, certifications, or instructions.</li>
            <li><strong>You are solely responsible</strong> for any decisions or actions you take (or do not take) based on information provided by Second Clear.</li>
            <li>Second Clear <strong>cannot and does not confirm</strong> whether any communication is fraudulent, legitimate, or safe.</li>
            <li>Second Clear is <strong>not a substitute</strong> for professional legal, financial, or law enforcement advice.</li>
          </Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>NATURE OF THE SERVICE</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>Second Clear provides <strong>advisory second-opinion reviews</strong> related to calls, messages, emails, or situations submitted by users.</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>Second Clear:</Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>Does <strong>not</strong> block, intercept, or control communications</li>
            <li>Does <strong>not</strong> make decisions on your behalf</li>
            <li>Does <strong>not</strong> guarantee outcomes or accuracy</li>
            <li>Does <strong>not</strong> provide legal, financial, or law-enforcement services</li>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>All decisions remain solely yours.</Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>1. ACCEPTANCE OF TERMS</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>By creating an account, accessing, or using Second Clear ("Service"), you agree to be bound by these Terms of Service ("Terms"), our Privacy Policy, and the mandatory arbitration agreement. If you are using Second Clear on behalf of another person (such as a family member), you represent that you have authority to bind that person to these Terms. These Terms constitute a legally binding agreement between you and Second Clear LLC ("Second Clear," "we," "us," or "our"), an Ohio Limited Liability Company.</Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>2. NATURE OF THE SERVICE</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>2.1 What Second Clear Does</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>Second Clear provides advisory second-opinion reviews of calls, messages, emails, voicemails, and other communications.</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>Our Service:</Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>Reviews content for patterns, signals, and observations associated with deceptive tactics.</li>
            <li>Analyzes voice patterns and metadata to identify signals associated with AI voice cloning or synthetic speech.</li>
            <li>Provides a "Level of Concern" based on cross-referencing against known fraud patterns and available databases.</li>
            <li>Offers training exercises to help you recognize common fraud patterns over time.</li>
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2 }}>2.2 What Second Clear Does NOT Do</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>Second Clear expressly does NOT: Block or control communications; Make decisions for you; Provide legal or financial advice; Provide law enforcement services; or Guarantee accuracy or specific outcomes.</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2 }}>2.3 Advisory Nature & The "Take a Second" Principle</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>All information is advisory. The absence of warning signs does not mean a communication is safe. The Service is an informational tool designed to provide you with the time necessary to pause before acting. "Clarity" refers to receiving data to assist your own judgement.</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2 }}>2.4 Third-Party Content Disclaimer</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>The Service may feature third-party content or public records for educational and analysis purposes. Second Clear does not own, endorse, or guarantee the accuracy of such content, and its use does not imply any affiliation with or endorsement by original creators or government agencies.</Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>3. INTELLECTUAL PROPERTY</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>All content, features, and functionality (including, but not limited to, the Deterministic Analysis Engine, the "5 Stops" framework, report formats, logos, and software) are the exclusive property of Second Clear LLC. You are granted a limited, personal, revocable license to use the Service. You may not copy, modify, reverse-engineer, or scrape any part of the Service.</Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>4. SUBMITTED CONTENT</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>By submitting content for review, you grant Second Clear a non-exclusive, world-wide, royalty-free license to use that data (fully anonymized) to improve our detection engine and for educational analysis. You represent that you have the right to submit such content.</Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>5. NO PROFESSIONAL RELATIONSHIP</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>Use of Second Clear does not create an attorney-client, fiduciary, or professional advisory relationship. We are an information service, not your lawyer, financial advisor, or agent.</Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>6. CAREGIVER AND SHARED ACCESS</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>If you use caregiver features or shared access tools, you represent and warrant that:</Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>You have obtained appropriate, informed consent from the person you are assisting.</li>
            <li>You agree to respect their autonomy and decision-making authority at all times.</li>
            <li>You are solely responsible for ensuring that your access to their communications is lawful and authorized.</li>
            <li>Second Clear is not responsible for disputes between users and the individuals they are assisting.</li>
          </Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>7. USER ELIGIBILITY & RESPONSIBILITIES</Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li><strong>7.1 Eligibility:</strong> You must be at least 18 years of age and a resident of the United States. If you create an account on behalf of another person, you represent that you have that person's consent and authority to bind them to these Terms.</li>
            <li><strong>7.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</li>
            <li><strong>7.3 Accuracy:</strong> You agree to provide accurate, current, and complete information. Any action you take (or do not take) based on Second Clear's reviews is entirely your own decision and responsibility.</li>
          </Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>8. COMMUNICATIONS AND MESSAGING (SMS/MMS)</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>By providing a phone number, you expressly consent to receive service-related communications, including "Level of Concern" reports, via SMS, MMS, and voice calls.</Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li><strong>Opt-Out:</strong> Reply STOP at any time to cancel. Opting out may prevent delivery of timely alerts.</li>
            <li><strong>Help:</strong> Reply HELP for assistance or email support@secondclear.com.</li>
            <li><strong>Rates:</strong> Message and data rates may apply. Message frequency varies.</li>
          </Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>9. SUBSCRIPTIONS AND PAYMENTS</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>Access requires a paid subscription. <strong>AT THE END OF A FREE TRIAL, YOUR SUBSCRIPTION WILL AUTOMATICALLY CONVERT TO A PAID SUBSCRIPTION AND YOU WILL BE CHARGED THE THEN-CURRENT RATE UNLESS CANCELLED.</strong> You may cancel at any time via your account dashboard.</Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>10. DISCLAIMER OF WARRANTIES & LIMITATION OF LIABILITY</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>SECOND CLEAR IS PROVIDED "AS IS." WE DO NOT GUARANTEE PROTECTION FROM FRAUD OR FINANCIAL LOSS. TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM OR $100.00, WHICHEVER IS GREATER.</Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>11. DISPUTE RESOLUTION & MANDATORY ARBITRATION</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}><strong>MANDATORY BINDING ARBITRATION:</strong> Any dispute arising out of these Terms shall be settled by binding arbitration in Columbus, Ohio, administered by the American Arbitration Association (AAA). <strong>YOU WAIVE YOUR RIGHT TO A JURY TRIAL OR TO PARTICIPATE IN A CLASS ACTION.</strong></Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }}>CONTACT</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>For questions regarding these Terms:</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Second Clear</strong> — Email: support@secondclear.com</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>SMS support messages are sent only in connection with your use of Second Clear. Reply STOP to opt out.</Typography>

          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary"><em>Second Clear® — All Rights Reserved — 2026</em></Typography>
        </Box>
        </Box>

        {/* Scroll Prompt */}
        {!hasScrolledToBottom && (
          <Box sx={{
            bgcolor: "#e3f2fd",
            borderLeft: "4px solid #1976d2",
            p: 1.5,
            mb: 2,
            borderRadius: 1,
            textAlign: "center",
          }}>
            <Typography variant="body2" fontWeight={600} color="primary.main">
              You must read through and scroll to the bottom of the Terms of Service before you can agree.
            </Typography>
          </Box>
        )}

        {/* Acknowledgment Checkboxes */}
        <Box sx={{
          opacity: hasScrolledToBottom ? 1 : 0.5,
          pointerEvents: hasScrolledToBottom ? "auto" : "none",
          transition: "opacity 0.3s ease",
          mt: 2,
        }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Please confirm you understand the following:
          </Typography>
          <FormGroup>
            {acknowledgmentItems.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={acknowledgments[key]}
                    onChange={() => handleAcknowledgmentChange(key)}
                    disabled={!hasScrolledToBottom}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{label}</Typography>}
                sx={{ alignItems: "flex-start", mb: 1, "& .MuiCheckbox-root": { pt: 0.25 } }}
              />
            ))}
          </FormGroup>
        </Box>
      </Box>
    );
  };

  const renderReview = () => {
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    const clientFullName = `${formData.clientFirstName} ${formData.clientLastName}`.trim();

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Review Your Order
        </Typography>

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Account Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formData.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formData.phone}
          </Typography>
        </Paper>

        {formData.accountType === "other" && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Person to Help
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {clientFullName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formData.clientPhone}
            </Typography>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: "#f5f7fa" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Second Clear Service
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formData.accountType === "self" ? "Personal account" : `Help for ${formData.clientFirstName}`}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="h5" fontWeight={600} color="primary">
                $15
              </Typography>
              <Typography variant="body2" color="text.secondary">
                /month
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Alert severity="info" sx={{ mb: 2 }}>
          Your card will be charged after your 7-day trial ends. Cancel anytime.
        </Alert>

      </Box>
    );
  };

  const isReviewStep = (formData.accountType === "self" && activeStep === 3) ||
                       (formData.accountType === "other" && activeStep === 4);

  const isTermsStep = (formData.accountType === "self" && activeStep === 2) ||
                      (formData.accountType === "other" && activeStep === 3);

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box ref={formTopRef} sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h4" color="primary" fontWeight={700}>
          Second Clear
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verify suspicious calls & texts
        </Typography>
      </Box>

      {statusLoading ? (
        <Paper elevation={2} sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Paper>
      ) : !signupsEnabled ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <HourglassEmptyIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Signups Temporarily Paused
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {pauseMessage || "We're not accepting new signups right now. Join the waitlist and we'll let you know when we reopen."}
          </Typography>

          {waitlistSuccess ? (
            <Alert severity="success" sx={{ textAlign: 'left' }}>
              You're on the list! We'll email you when signups reopen.
            </Alert>
          ) : (
            <>
              {waitlistError && <Alert severity="error" sx={{ mb: 2 }}>{waitlistError}</Alert>}
              <TextField
                fullWidth
                label="Name (optional)"
                value={waitlistName}
                onChange={(e) => setWaitlistName(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={waitlistEmail}
                onChange={(e) => { setWaitlistEmail(e.target.value); setWaitlistError(''); }}
                margin="normal"
                required
              />
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleWaitlistSubmit}
                disabled={waitlistSubmitting}
                sx={{ mt: 2 }}
              >
                {waitlistSubmitting ? <CircularProgress size={24} /> : 'Join Waitlist'}
              </Button>
            </>
          )}
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 4 }}>
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
              },
            }}
            alternativeLabel
          >
            {currentSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {renderStepContent()}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>

            {isReviewStep ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                size="large"
              >
                {loading ? <CircularProgress size={24} /> : "Start 7-Day Free Trial"}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={validating || (isTermsStep && !(hasScrolledToBottom && allAcknowledged))}
              >
                {validating ? <CircularProgress size={22} /> : "Continue"}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
        Already have an account?{" "}
        <Link href={process.env.REACT_APP_DASHBOARD_URL || "https://dashboard.secondclear.com"}>
          Sign in
        </Link>
      </Typography>
    </Container>
  );
};

export default Signup;